import { Radio } from "@base-ui/react/radio";
import { RadioGroup } from "@base-ui/react/radio-group";
import { type MouseEvent, useCallback } from "react";
import { cx } from "../../lib/cx";

/**
 * One option or none, as a row of pill chips, on Base UI's RadioGroup: one
 * tab stop, and the arrows move the selection, as `Segmented` does.
 *
 * What neither neighbour gives: `Segmented` always holds a value, and
 * `MultiChips` holds any number. Here the value is one option or `null`, and
 * with `clearable` a press on the selected chip — a tap, or Space — gives
 * `null` back. A radio never unchecks itself, so the clear is this
 * component's own click handler; Base UI then clicks a radio that is already
 * checked, which changes nothing.
 *
 * `scroll` keeps the chips on one line that scrolls sideways. A scroller
 * clips at its padding box, so the row is padded by exactly the focus ring's
 * reach (2 px outline + 2 px offset) and pulled back by the same margin, and
 * the same reach is its scroll padding, so a chip the arrows scroll into view
 * stops short of the edge by its ring. Neither end's ring is cut off, and the
 * chips still line up with the text above them.
 *
 * The selected chip is the inverse pill, as in `MultiChips`.
 */

export interface ChipGroupOption<T extends string> {
  label: string;
  value: T;
}

export interface ChipGroupProps<T extends string> {
  className?: string;
  /** A press on the selected chip clears the value to `null`. */
  clearable?: boolean;
  disabled?: boolean;
  /** The group's accessible name. */
  label: string;
  onValueChange: (value: T | null) => void;
  options: readonly ChipGroupOption<T>[];
  /** One line that scrolls sideways, instead of wrapping. */
  scroll?: boolean;
  value: T | null;
}

export function ChipGroup<T extends string>({
  value,
  onValueChange,
  options,
  label,
  clearable,
  scroll,
  disabled,
  className,
}: ChipGroupProps<T>) {
  // One handler for every chip: the chip pressed is `currentTarget`, and its
  // `aria-checked` is still the state from before this press.
  const clear = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (
        clearable &&
        !disabled &&
        event.currentTarget.getAttribute("aria-checked") === "true"
      ) {
        onValueChange(null);
      }
    },
    [clearable, disabled, onValueChange]
  );
  return (
    <RadioGroup<T | null>
      aria-label={label}
      className={cx(
        "flex gap-2",
        scroll
          ? "a-no-scrollbar -m-1 scroll-px-1 overflow-x-auto p-1"
          : "flex-wrap",
        className
      )}
      disabled={disabled}
      onValueChange={onValueChange}
      value={value}
    >
      {options.map((option) => (
        <Radio.Root
          className="a-press inline-flex h-12 shrink-0 cursor-pointer select-none items-center whitespace-nowrap rounded-full bg-surface px-4 font-medium text-base text-ink-2 ring-1 ring-line transition-colors hover:ring-line-strong focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2 data-disabled:cursor-not-allowed data-checked:bg-inverse data-checked:text-on-inverse data-disabled:opacity-40 data-checked:ring-0"
          key={option.value}
          onClick={clear}
          value={option.value}
        >
          {option.label}
        </Radio.Root>
      ))}
    </RadioGroup>
  );
}
